import { useBoolean } from "@fluentui/react-hooks";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

const useClientStorage = <T>(
  key: string,
  storageName: string,
  initialState: T,
  storage: Storage):
  [Partial<T>, { setState: Dispatch<SetStateAction<Partial<T>>>, resetState: () => void }] => {
  const [state, setState] = useState<Partial<T>>(initialState);
  const resetState = useCallback(() => setState({ ...initialState }), []);
  const [initializing, { setFalse: initializationDone }] = useBoolean(true);

  useEffect(
    () => {
      if (initializing && key && initialState) {
        console.info(`[${key}]: Using ${storageName} storage`);
        console.info(`[${key}]: Initial State: `, initialState);

        const value = storage.getItem(key);

        if (!value) {
          console.info(`[${key}]: Item doesn't exist in the ${storageName} storage. Adding key with initial value.`);
          storage.setItem(key, JSON.stringify(initialState));
        }

        setState(JSON.parse(value) as T);
        initializationDone();
      }
    },
    [key, initialState]
  );

  useEffect(
    () => {
      if (!initializing && state) {
        const value = JSON.stringify(state);
        console.log(`[${key}]: ${value}`);
        storage.setItem(key, value);
      }
    },
    [state]
  );

  return [state, { setState, resetState } as const];
};

export const useLocalStorage = <T>(key: string, initialValue: T) => useClientStorage(key, 'Local', initialValue, localStorage);
export const useSessionStorage = <T>(key: string, initialValue: T) => useClientStorage(key, 'Session', initialValue, sessionStorage);